import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { useParams, Link, useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Breadcrumb from '../../../common/Breadcrumb';
import StepIndicator from './StepIndicator';
import AudioPlayer from './AudioPlayer';
import SocialPostCard from './SocialPostCard';
import TldrDisplay from './TldrDisplay';
import ErrorBoundary from './ErrorBoundary';

/**
 * Results Page Component
 * Displays all generated content for an article
 */
const ResultsPage = () => {
  const { slug } = useParams();
  const history = useHistory();
  const location = useLocation();
  const creditInfo = location.state?.creditInfo;
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHideModal, setShowHideModal] = useState(false);
  const [hideReason, setHideReason] = useState('');
  const [hiding, setHiding] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [hiddenTitle, setHiddenTitle] = useState('');
  const [adminViewingHidden, setAdminViewingHidden] = useState(false);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  // Check if content appears incomplete (less than 500 chars)
  const isContentIncomplete = article?.article_content && article.article_content.length < 500;

  const fetchArticle = async () => {
    try {
      const response = await fetch(`/api/tools/article/${slug}`);
      const data = await response.json();

      if (data.error) {
        // Check if article is hidden/under review
        if (data.isHidden) {
          setIsUnderReview(true);
          setHiddenTitle(data.title || '');
        }
        setError(data.error);
        setLoading(false);
        return;
      }

      setArticle(data.article);
      if (data.adminViewingHidden) {
        setAdminViewingHidden(true);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to load article');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleHideArticle = async () => {
    setHiding(true);
    try {
      const response = await fetch(`/api/tools/article/${slug}/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: hideReason })
      });
      const data = await response.json();
      if (data.success) {
        setShowHideModal(false);
        history.push('/tools/article-to-media');
      } else {
        setError(data.error || 'Failed to hide article');
      }
    } catch (err) {
      setError('Failed to hide article');
    } finally {
      setHiding(false);
    }
  };

  const handleReportIncomplete = async () => {
    setFeedbackSubmitting(true);
    try {
      const response = await fetch(`/api/tools/article/${slug}/report-incomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentLength: article.article_content?.length || 0,
          sourceUrl: article.source_url
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowFeedbackModal(false);
        setFeedbackSubmitted(true);
      } else {
        setError(data.error || 'Failed to submit feedback');
      }
    } catch (err) {
      setError('Failed to submit feedback');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }} className="text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading results...</p>
      </Container>
    );
  }

  if (error) {
    // Special display for articles under review
    if (isUnderReview) {
      return (
        <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }}>
          <Alert variant="info">
            <Alert.Heading>Article Under Review</Alert.Heading>
            {hiddenTitle && <p><strong>{hiddenTitle}</strong></p>}
            <p className="mb-0">
              This article may be incomplete, so Elliot's looking into it.
            </p>
          </Alert>
          <Link to="/tools/article-to-media">
            <Button variant="outline-primary">Process Another Article</Button>
          </Link>
        </Container>
      );
    }

    return (
      <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }}>
        <Alert variant="danger">{error}</Alert>
        <Link to="/tools/article-to-media">
          <Button variant="outline-primary">Back to Tool</Button>
        </Link>
      </Container>
    );
  }

  if (!article) {
    return (
      <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }}>
        <Alert variant="warning">Article not found</Alert>
        <Link to="/tools/article-to-media">
          <Button variant="outline-primary">Back to Tool</Button>
        </Link>
      </Container>
    );
  }

  const breadcrumbItems = [
    { label: 'Tools', path: '/tools' },
    { label: 'Article to Media', path: '/tools/article-to-media' },
    { label: 'Results', path: `/tools/article-to-media/${slug}` }
  ];

  return (
    <>
      <Helmet>
        <title>{article.meta_title || `${article.article_title} | Article to Media`}</title>
        <meta name="description" content={article.meta_description || article.tldr_content?.[0]} />
        <meta property="og:title" content={article.article_title} />
        <meta property="og:description" content={article.meta_description || article.tldr_content?.[0]} />
        {article.og_image_url && <meta property="og:image" content={article.og_image_url} />}
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://elliotkoss.com/tools/article-to-media/${slug}`} />
      </Helmet>

      <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }}>
        <Breadcrumb items={breadcrumbItems} />
        <StepIndicator currentStep={3} />

        {/* Admin notice for hidden articles */}
        {adminViewingHidden && (
          <Alert variant="warning" className="mb-4">
            <strong>Admin View:</strong> This article is hidden and under review.
            {article.hidden_reason && <span> Reason: "{article.hidden_reason}"</span>}
          </Alert>
        )}

        {/* Credit info notice - when user's code ran out and we used public credits */}
        {creditInfo?.usedFallback && (
          <Alert variant="info" className="mb-4">
            <i className="bi bi-info-circle me-2"></i>
            Your access code{creditInfo.privateCodeName ? ` (${creditInfo.privateCodeName})` : ''} didn't have enough credits for today.
            This article was generated using free public credits.
          </Alert>
        )}

        {/* Header */}
        <Row className="mb-4">
          <Col>
            <h1 style={{ fontSize: '1.75rem' }}>{article.article_title}</h1>
            <div className="d-flex flex-wrap gap-3 align-items-center text-muted">
              {article.article_outlet && <span>{article.article_outlet}</span>}
              {article.article_byline && <span>• {article.article_byline}</span>}
            </div>
            <div className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
              Generated on {formatDate(article.completed_at)}
            </div>
            <div className="mt-3">
              <a
                href={article.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary btn-sm"
              >
                View Original Article
              </a>
            </div>
            <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
              These are AI-generated samples based on the original article content.
            </p>
          </Col>
        </Row>

        {/* TLDR & Audio Section - side by side on lg+, stacked on smaller */}
        <Row className="mb-4">
          {article.tldr_content && (
            <Col lg={6} className="mb-4 mb-lg-0">
              <TldrDisplay bullets={article.tldr_content} />
            </Col>
          )}

          {article.audio && (
            <Col lg={6}>
              <Card className="h-100">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">30-Second Audio Preview</h5>
                  {(() => {
                    const isDryRun = article.audio.s3_url?.includes('example.com/mock');
                    const handleDownloadAudio = () => {
                      if (isDryRun) return;
                      const filename = `${article.slug}-audio-preview.mp3`;
                      const downloadUrl = `/api/tools/article/download?url=${encodeURIComponent(article.audio.s3_url)}&filename=${encodeURIComponent(filename)}`;
                      window.location.href = downloadUrl;
                    };
                    return (
                      <button
                        onClick={handleDownloadAudio}
                        disabled={isDryRun}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          backgroundColor: '#f8f9fa',
                          color: '#495057',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          cursor: isDryRun ? 'not-allowed' : 'pointer',
                          opacity: isDryRun ? 0.5 : 1,
                          transition: 'all 0.2s ease'
                        }}
                        title={isDryRun ? 'Audio not available in test mode' : 'Download audio'}
                      >
                        <i className="bi bi-download"></i>
                      </button>
                    );
                  })()}
                </Card.Header>
                <Card.Body>
                  <AudioPlayer
                    src={article.audio.s3_url}
                    slug={article.slug}
                  />
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>

        {/* Social Posts Section */}
        {article.socialPosts && article.socialPosts.length > 0 && (
          <Row className="mb-4">
            <Col>
              <h4 className="mb-3">Social Media Posts</h4>
              <Row>
                {article.socialPosts.map((post, index) => (
                  <Col lg={4} md={6} key={post.id} className="mb-4">
                    <SocialPostCard
                      post={post}
                      articleSlug={article.slug}
                      postNumber={index + 1}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        )}

        {/* Article Content Section */}
        {article.article_content && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Extracted Article Content</h5>
                  <small className="text-muted">
                    {article.article_content?.length || 0} characters extracted
                  </small>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Title:</strong> {article.article_title}
                  </div>
                  {article.article_byline && (
                    <div className="mb-3">
                      <strong>Byline:</strong> {article.article_byline}
                    </div>
                  )}
                  {article.published_date && (
                    <div className="mb-3">
                      <strong>Published:</strong> {formatDate(article.published_date)}
                    </div>
                  )}
                  <hr />
                  <div style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                    lineHeight: '1.6'
                  }}>
                    {article.article_content}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Incomplete Content Warning */}
        {isContentIncomplete && !feedbackSubmitted && (
          <Row className="mb-4">
            <Col>
              <Alert variant="warning">
                <Alert.Heading style={{ fontSize: '1rem' }}>
                  Content May Be Incomplete
                </Alert.Heading>
                <p className="mb-2">
                  The extracted content appears shorter than expected ({article.article_content?.length || 0} characters).
                  Some websites require additional loading steps to show full content.
                </p>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => setShowFeedbackModal(true)}
                >
                  Report Incomplete Content
                </Button>
              </Alert>
            </Col>
          </Row>
        )}

        {feedbackSubmitted && (
          <Row className="mb-4">
            <Col>
              <Alert variant="success">
                Thank you for your feedback! We'll improve our extraction for this website.
              </Alert>
            </Col>
          </Row>
        )}

        {/* CTA */}
        <Row className="mt-5 mb-4">
          <Col className="text-center">
            <hr className="mb-4" />
            <p className="text-muted">Want to process another article?</p>
            <div className="d-flex justify-content-center gap-3">
              <Link to="/tools/article-to-media">
                <Button variant="primary" size="lg">
                  Process Another Article
                </Button>
              </Link>
              {!article.is_reviewed && (
                <Button
                  variant="outline-danger"
                  size="lg"
                  onClick={() => setShowHideModal(true)}
                >
                  Delete Article
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Hide Article Modal */}
      <Modal show={showHideModal} onHide={() => setShowHideModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Article</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This will hide the article from public view while Elliot reviews it. In general, only bad outputs will be removed, but please give any useful context to help with this decision.</p>
          <Form.Group>
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Why should this article be deleted?"
              value={hideReason}
              onChange={(e) => setHideReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHideModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleHideArticle} disabled={hiding}>
            {hiding ? 'Hiding...' : 'Delete Article'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Incomplete Content Feedback Modal */}
      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Report Incomplete Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            It looks like we may not have extracted the full article content. This often happens when
            websites use "Read More" buttons that load content dynamically.
          </p>
          <p>
            By reporting this, you'll help us improve our extraction system for <strong>{article?.source_domain}</strong>.
          </p>
          <div className="bg-light p-3 rounded">
            <small className="text-muted">
              <strong>Current content:</strong> {article?.article_content?.length || 0} characters<br />
              <strong>Source:</strong> {article?.source_url}
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFeedbackModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleReportIncomplete} disabled={feedbackSubmitting}>
            {feedbackSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Wrap with ErrorBoundary for graceful error handling
const ResultsPageWithErrorBoundary = () => (
  <ErrorBoundary>
    <ResultsPage />
  </ErrorBoundary>
);

export default ResultsPageWithErrorBoundary;
